import { Buffer } from 'buffer';
import process from 'process';

import {
  FingerprintJSPro,
  FpjsProvider,
} from '@fingerprintjs/fingerprintjs-pro-react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';

import AppRouter from './app/router';
import { PageSkeleton } from './components/ui/page-skeleton';
import './index.scss';
import './lib/i18n';

const AppProvider = lazy(() =>
  import('@/context/provider').then((module) => ({
    default: module.AppProvider,
  })),
);
const Toaster = lazy(() =>
  import('./components/ui/sonner').then((module) => ({
    default: module.Toaster,
  })),
);

window.global ||= window;
window.process = process;
window.Buffer = Buffer;

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  if (typeof reason === 'number') {
    event.preventDefault();
    console.warn('[Unhandled ErrorCode rejection]', reason);
  }
});

if (!import.meta.env.DEV && import.meta.env.VITE_ENV === 'production') {
  const initPosthog = () => {
    import('posthog-js').then(({ default: posthog }) => {
      const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
      const apiHost = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

      if (!apiKey || !apiHost) return;
      posthog.init(apiKey, {
        api_host: apiHost,
        capture_pageview: true,
        persistence: 'cookie',
      });
      // Expose and notify readiness to avoid eagerly importing the SDK elsewhere
      (window as any).posthog = posthog;
      const readyEvent = new CustomEvent('posthog:ready', { detail: posthog });
      window.dispatchEvent(readyEvent);
    });
  };

  (window as any).requestIdleCallback
    ? (window as any).requestIdleCallback(initPosthog)
    : setTimeout(initPosthog, 0);
}

const root = document.getElementById('root');
if (!root) throw new Error('No root element found');

createRoot(root).render(
  <StrictMode>
    <FpjsProvider
      loadOptions={{
        apiKey: import.meta.env.VITE_FPJS_API_KEY,
        endpoint: [
          import.meta.env.VITE_FPJS_ENDPOINT,
          FingerprintJSPro.defaultEndpoint,
        ],
        scriptUrlPattern: [
          import.meta.env.VITE_FPJS_SCRIPT_URL_PATTERN,
          FingerprintJSPro.defaultScriptUrlPattern,
        ],
      }}
    >
      <Suspense fallback={<PageSkeleton />}>
        <AppProvider>
          <AppRouter />
        </AppProvider>
      </Suspense>
    </FpjsProvider>
    <Suspense fallback={null}>
      <Toaster closeButton />
    </Suspense>

    {!import.meta.env.DEV && import.meta.env.VITE_ENV === 'production' && (
      <SpeedInsights />
    )}
  </StrictMode>,
);
